import React, { ChangeEvent, FunctionComponent, useEffect, useState } from 'react';
import {
    Button,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Grid,
    makeStyles,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableFooter,
    FormHelperText,
    Checkbox,
    FormControlLabel,
} from '@material-ui/core';
import gray from '@material-ui/core/colors/grey';
import { Skeleton } from '@material-ui/lab';
import classNames from 'classnames';
import moment from 'moment';
import DatePicker from '../DatePicker';
import { JournalEntry, JournalEntryList } from '../../store/modules/accounting/types';
import InputTextComponent from '../InputTextField';
import { displayCurrencyAmount } from '../../utils/utils';
import {
    ACCOUNTING_ACCOUNTS_RECEIVABLE,
    ACCOUNTING_GENERAL_ASSETS,
    FEATURE_EXTERNAL_PAYMENT,
} from '../../store/app.constants';
import { DropDownField, PaymentType } from '../../store/modules/application/types';
import AutoCompleteSelect from '../AutoCompleteSelect';
import { useAuthenticationContext } from 'src/authentication';
import { Api } from 'src/store/services/services';
import { round } from 'lodash';
const formSubmit = require('dom-form-submit');

type ReceivePaymentDialogProps = {
    personId: string;
    licenseId?: string;
    onClose: Function;
    onSave: Function;
    updating?: boolean;
    journalEntries: JournalEntryList;
    journalEntriesLoading: boolean;
    getJournalEntries: Function;
    featureFlags: string[];
};

type InvoiceSelection = {
    journalEntry: JournalEntry;
    selected: boolean;
    amount: string;
};

const paymentMethods = [
    {
        label: 'Cash',
        value: 'Cash',
    },
    {
        label: 'Check',
        value: 'Check',
    },
    {
        label: 'Credit Card',
        value: 'CreditCard',
    },
    {
        label: 'eCheck',
        value: 'eCheck',
    },
];

function sanitizeAmount(amount: number) {
    return round(amount, 2);
}

function getInvoiceBalance(journalEntry: JournalEntry): number {
    if (!journalEntry.invoicing) {
        return 0;
    }

    return sanitizeAmount(journalEntry.invoicing.amountInvoiced - journalEntry.invoicing.amountPaid);
}

function compareByDueDate(journalEntry1: JournalEntry, journalEntry2: JournalEntry): number {
    const dueDate1 = journalEntry1.invoicing?.dueDate ?? '';
    const dueDate2 = journalEntry2.invoicing?.dueDate ?? '';

    if (dueDate1 && !dueDate2) {
        return -1;
    }

    if (!dueDate1 && dueDate2) {
        return 1;
    }

    return dueDate1.localeCompare(dueDate2);
}

function getTotalAllocated(invoices: InvoiceSelection[]): number {
    return invoices.reduce((accum, invoice) => accum + (invoice.amount ? parseFloat(invoice.amount) : 0), 0);
}

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        color: theme.palette.text.secondary,
        fontSize: 20,
        fontWeight: 500,
        marginBottom: 20,
    },
    dialogContent: {
        minHeight: 500,
    },
    divider: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
    },
    contentHeading: {
        marginTop: theme.spacing(4),
    },
    tableRow: {},
    tableFooterCell: {
        borderBottomWidth: 0,
    },
    label: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,
    },
    standardLabel: {
        textAlign: 'right',
        paddingLeft: '5px',
        marginRight: 20,
    },
    externalPaymentLabel: {
        marginLeft: 35,
    },
    summaryLabel: {
        textAlign: 'right',
    },
    datePicker: {
        margin: 0,
    },
    datePickerContainer: {
        backgroundColor: 'white',
        borderRadius: 5,
        justifyContent: 'flex-start',
    },
    datePickerInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        paddingTop: 7,
        paddingBottom: 3,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
        width: '100%',
        '&:before': {
            borderBottomColor: 'white',
        },
        '&:after': {
            borderBottomColor: 'white',
        },
        '&:hover:before': {
            borderBottomColor: ['white', '!important'],
        },
    },
    selectInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
        width: '100%',
    },
    textField: {
        fontSize: 14,
        fontWeight: 450,
        color: gray[600],
        lineHeight: '21px',
        wordWrap: 'break-word',
        width: '100%',
    },
    textFieldInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        padding: 10,
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
    },
    textFieldInputDisabled: {
        backgroundColor: gray[300],
    },
    amountInput: {
        textAlign: 'right',
    },
    headerWeight: {
        fontWeight: 'bold',
    },
}));

const ReceivePaymentDialog: FunctionComponent<ReceivePaymentDialogProps> = (props) => {
    const {
        personId,
        licenseId,
        onClose,
        onSave,
        updating,
        journalEntries,
        journalEntriesLoading,
        getJournalEntries,
        featureFlags,
    } = props;

    const [date, setDate] = useState(moment().format());
    const [paymentAmount, setPaymentAmount] = useState('');
    const [initialDisplay, setInitialDisplay] = useState(true);
    const [paymentMethod, setPaymentMethod] = useState<DropDownField | null>(null);
    const [refNo, setRefNo] = useState('');
    const [description, setDescription] = useState('');
    const [dateInvalid, setDateInvalid] = useState(false);
    const [allInvoices, setAllInvoices] = useState(false);
    const [invoices, setInvoices] = useState<InvoiceSelection[]>([]);
    const [externalPaymentSelected, setExternalPaymentSelected] = useState<boolean>(false);

    const { scopes, getAccessToken } = useAuthenticationContext();

    const getTotalSelectedBalance = () => {
        return invoices
            .filter((invoice) => invoice.selected)
            .map((invoice) => getInvoiceBalance(invoice.journalEntry))
            .reduce((acc, amount) => acc + amount, 0);
    };

    const [totalSelectedBalance, setTotalSelectedBalance] = useState<number>(getTotalSelectedBalance());

    const classes = useStyles();

    const standardLabel = classNames(classes.label, classes.standardLabel);
    const externalPaymentLabel = classNames(classes.label, classes.externalPaymentLabel);
    const summaryLabel = classNames(classes.label, classes.summaryLabel);

    useEffect(() => {
        getJournalEntries({
            filter: {
                where: {
                    type: 'Invoice',
                    [licenseId ? 'lineItems.license.id' : 'lineItems.person.id']: licenseId ?? personId,
                    'invoicing.status': 'Open',
                },
            },
        });
    }, [personId, licenseId, getJournalEntries]);

    useEffect(() => {
        if (journalEntriesLoading) {
            setInvoices([]);
            setAllInvoices(false);
        } else {
            const sortedInvoices = (journalEntries ?? []).sort(compareByDueDate);

            setInvoices(sortedInvoices.map((journalEntry) => ({ journalEntry, selected: true, amount: '' })));
            setAllInvoices(true);
        }
    }, [journalEntries, journalEntriesLoading]);

    const paymentAmountValue = paymentAmount ? parseFloat(paymentAmount) : 0;

    let isValid = !!paymentAmountValue;

    if (isValid) {
        isValid = externalPaymentSelected
            ? paymentAmountValue <= totalSelectedBalance && invoices.some((invoice) => invoice.selected)
            : !dateInvalid;
    }

    const allocatedTotal = getTotalAllocated(invoices);

    const changeDate = (date: string) => {
        setDate(date);
        setDateInvalid(!moment(date).isValid());
    };

    const changePaymentMethod = (data: DropDownField | null) => {
        setPaymentMethod(data);
        if (!!data?.value) {
            setDescription(`${data.value} payment`);
        } else {
            setDescription('');
        }
    };

    const allocatePayment = (amount: number, invoices: InvoiceSelection[]) => {
        for (const invoice of invoices.filter((inv) => inv.selected)) {
            // If amount is zero then specifically set the invoice
            // amount to zero. This is necessary if initially a
            // higher payment amount was entered, populating all
            // invoices with amounts and then the payment amount
            // was modified to a lower amount that doesn't cover
            // all of the invoices.
            if (!amount) {
                invoice.amount = '';
            }

            const invoiceBalance = getInvoiceBalance(invoice.journalEntry);

            if (amount < invoiceBalance) {
                invoice.amount = amount.toFixed(2);

                amount = 0;
            } else {
                invoice.amount = invoiceBalance.toFixed(2);

                amount -= invoiceBalance;
            }
        }
    };

    const changePaymentAmount = (amount: string) => {
        if (invoices) {
            const amountNumber = amount ? parseFloat(amount) : 0;

            allocatePayment(amountNumber, invoices);
        }

        if (amount) {
            // Once an amount has been entered, remove initial display flag.  This
            // means we'll display an error message if the amount is blanked out.
            setInitialDisplay(false);
        }

        setPaymentAmount(amount);

        setTotalSelectedBalance(getTotalSelectedBalance());
    };

    const toggleAllInvoices = () => {
        const updatedSelection = !allInvoices;
        const updatedInvoices = [...invoices];

        updatedInvoices.forEach((inv) => {
            inv.selected = updatedSelection;

            if (!inv.selected) {
                inv.amount = '';
            }
        });

        allocatePayment(paymentAmountValue, updatedInvoices);

        setInvoices(updatedInvoices);
        setAllInvoices(updatedSelection);

        setTotalSelectedBalance(getTotalSelectedBalance());
    };

    const toggleInvoice = (id: string) => {
        const updatedInvoices = [...invoices];
        const invoice = updatedInvoices.find((invoice) => invoice.journalEntry.id === id);

        if (invoice) {
            invoice.selected = !invoice.selected;

            if (!invoice.selected) {
                invoice.amount = '';
            }

            allocatePayment(paymentAmountValue, updatedInvoices);

            setInvoices(updatedInvoices);
            setAllInvoices(!updatedInvoices.some((inv) => !inv.selected));

            setTotalSelectedBalance(getTotalSelectedBalance());
        }
    };

    const updateInvoiceAmount = (id: string, amount: string) => {
        const updatedInvoices = [...invoices];
        const invoice = updatedInvoices.find((inv) => inv.journalEntry.id === id);

        if (invoice) {
            if (amount) {
                const baseAllocated = getTotalAllocated(updatedInvoices.filter((inv) => inv !== invoice));
                const paymentBalance = paymentAmountValue - baseAllocated;
                const invoiceBalance = getInvoiceBalance(invoice.journalEntry);
                const amountValue = parseFloat(amount);

                // Can't be more than invoiced balance or the remaining payment balance.
                const maxAmount = Math.min(invoiceBalance, paymentBalance);

                if (amountValue > maxAmount) {
                    amount = maxAmount.toFixed(2);
                }
            }

            invoice.amount = amount;

            setInvoices(updatedInvoices);
        }
    };

    const toggleExternalPaymentSelected = (e: ChangeEvent<HTMLInputElement>) => {
        setExternalPaymentSelected(e.target.checked);
    };

    const handleExternalPayment = async () => {
        const token = (await getAccessToken({ scopes: [scopes['payments.write']] })).accessToken;

        const returnUrl = `${window.location.href}`;

        const items: {
            journalEntryId: string;
            fees: { type: { id: string; name: string }; description: string; amount: number }[];
        }[] = [];

        for (const invoice of invoices.filter((inv) => parseFloat(inv.amount || '0'))) {
            const fees = [];
            let remainingBalance = parseFloat(invoice.amount);
            let amountPaidRemaining = sanitizeAmount(invoice.journalEntry.invoicing?.amountPaid ?? 0);

            const chargeableLineItems = invoice.journalEntry.lineItems.filter(
                (lineItem) => lineItem.type === 'credit' && lineItem.feeType && lineItem.account.type === 'Revenue',
            );

            for (const lineItem of chargeableLineItems) {
                let feeAmount = sanitizeAmount(lineItem.amount);

                // First, handle the amount already paid. Any amount
                // paid should be subtracted from the line items before
                // allocating the payment amount.
                if (amountPaidRemaining > 0) {
                    feeAmount = sanitizeAmount(feeAmount - amountPaidRemaining);

                    amountPaidRemaining = sanitizeAmount(amountPaidRemaining - lineItem.amount);

                    // If there is no remaining line item amount,
                    // continue to the next line item.
                    if (feeAmount <= 0) {
                        continue;
                    }
                }

                // The second step is to allocate the payment
                // amount to the line item.

                // If the remaining balance is less than the
                // line item amount than set the fee amount
                // to the remaining balance.
                if (remainingBalance < feeAmount) {
                    feeAmount = remainingBalance;
                }

                fees.push({
                    type: lineItem.feeType!,
                    description: lineItem.feeType!.name,
                    amount: feeAmount,
                });

                remainingBalance = sanitizeAmount(remainingBalance - feeAmount);

                if (!remainingBalance) {
                    break;
                }
            }

            items.push({
                journalEntryId: invoice.journalEntry.id,
                fees,
            });
        }

        const response = await Api.createPayment(token, {
            order: { items },
            context: {
                isPointOfSale: true,
                successUrl: returnUrl,
                cancelUrl: returnUrl,
            },
        });

        const transferData = response.data.transferData;

        formSubmit(transferData.parameters, {
            action: transferData.transferUrl,
            method: transferData.transferMethod,
        });
    };

    const handleAdd = async () => {
        if (dateInvalid) {
            return;
        }

        // If the external payment checkbox is selected,
        // handle that and short-circuit this function.
        if (externalPaymentSelected) {
            await handleExternalPayment();

            return;
        }

        const journalEntry: Omit<JournalEntry, 'id'> = {
            type: 'Payment',
            date: moment(date).format('YYYY-MM-DD'),
            description,
            payment: {
                type: paymentMethod?.value ? (paymentMethod.value as PaymentType) : undefined,
                referenceNumber: refNo,
                amount: paymentAmountValue,
                invoices: invoices
                    .filter((inv) => inv.selected && inv.amount)
                    .map((inv) => ({
                        id: inv.journalEntry.id,
                        invoiceNumber: inv.journalEntry.invoicing!.invoiceNumber,
                        amountApplied: parseFloat(inv.amount),
                    })),
            },
            lineItems: [],
        };

        journalEntry.lineItems.push({
            type: 'credit',
            amount: paymentAmountValue,
            description,
            account: {
                id: ACCOUNTING_ACCOUNTS_RECEIVABLE,
                name: 'Accounts Receivable',
                type: 'Asset',
            },
        });

        journalEntry.lineItems.push({
            type: 'debit',
            amount: paymentAmountValue,
            description,
            account: {
                id: ACCOUNTING_GENERAL_ASSETS,
                name: 'General Assets',
                type: 'Asset',
            },
        });

        onSave(journalEntry);
    };

    return (
        <>
            <DialogTitle className={classes.dialogTitle}>Receive Payment</DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Grid container alignItems="baseline">
                    <Grid item xs={2}>
                        <Typography className={standardLabel}>Date</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <DatePicker
                            containerClasses={classes.datePickerContainer}
                            datePickerClasses={classes.datePicker}
                            inputProps={{
                                classes: {
                                    root: classes.datePickerInput,
                                },
                            }}
                            label=""
                            value={date}
                            error={dateInvalid}
                            handleDate={changeDate}
                            disabled={externalPaymentSelected}
                        />
                        <FormHelperText error>{!date ? 'Date is required' : ' '}</FormHelperText>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography className={standardLabel}>Amount</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <InputTextComponent
                            id="amount"
                            size="small"
                            fontSize="13"
                            className={classes.textField}
                            inputProps={classNames(classes.textFieldInput, classes.amountInput)}
                            onChange={changePaymentAmount}
                            value={paymentAmount}
                            error={
                                (!paymentAmount && !initialDisplay) ||
                                (externalPaymentSelected && parseFloat(paymentAmount) > totalSelectedBalance)
                            }
                            inputType={'currency'}
                        />
                        <FormHelperText error>
                            {!paymentAmount && !initialDisplay
                                ? 'Amount is required'
                                : externalPaymentSelected && parseFloat(paymentAmount) > totalSelectedBalance
                                ? 'Cannot exceed invoice total'
                                : ' '}
                        </FormHelperText>
                    </Grid>
                    {featureFlags.includes(FEATURE_EXTERNAL_PAYMENT) ? (
                        <>
                            <Grid item xs={4}>
                                <FormControlLabel
                                    classes={{ label: externalPaymentLabel }}
                                    label="Make an external payment"
                                    labelPlacement="start"
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={externalPaymentSelected}
                                            onChange={(e) => toggleExternalPaymentSelected(e)}
                                            disabled={!invoices.length}
                                        />
                                    }
                                />
                            </Grid>
                        </>
                    ) : (
                        <Grid item xs={4} />
                    )}
                    <Grid item xs={2}>
                        <Typography className={standardLabel}>Payment Method</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <AutoCompleteSelect
                            id="inspection-type-select"
                            data={paymentMethods}
                            value={paymentMethod}
                            onChange={changePaymentMethod}
                            inputSize={'small'}
                            variant={'outlined'}
                            ariaLabel="Payment Method"
                            disabled={externalPaymentSelected}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <Typography className={standardLabel}>Reference Number</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <InputTextComponent
                            id="refNo"
                            disabled={externalPaymentSelected}
                            size="small"
                            fontSize="13"
                            className={classes.textField}
                            inputProps={classNames(classes.textFieldInput, classes.amountInput)}
                            onChange={setRefNo}
                            value={refNo}
                        />
                        <FormHelperText> </FormHelperText>
                    </Grid>
                    <Grid item xs={4} />
                    <Grid item xs={2}>
                        <Typography className={standardLabel}>Description</Typography>
                    </Grid>
                    <Grid item xs={featureFlags.includes(FEATURE_EXTERNAL_PAYMENT) ? 6 : 8}>
                        <InputTextComponent
                            id="description"
                            disabled={externalPaymentSelected}
                            size="small"
                            fontSize="13"
                            className={classes.textField}
                            inputProps={classes.textFieldInput}
                            onChange={setDescription}
                            value={description}
                        />
                    </Grid>
                </Grid>
                <Divider className={classes.divider} />
                <Typography className={classes.contentHeading}>Apply payment towards:</Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width="5%" className={classes.headerWeight}>
                                    <Checkbox color="primary" checked={allInvoices} onChange={toggleAllInvoices} />
                                </TableCell>
                                <TableCell className={classes.headerWeight}>Invoice</TableCell>
                                <TableCell className={classes.headerWeight} width="15%">
                                    Due Date
                                </TableCell>
                                <TableCell className={classes.headerWeight} align="right" size="small" width="20%">
                                    Invoiced Amount
                                </TableCell>
                                <TableCell className={classes.headerWeight} align="right" size="small" width="15%">
                                    Balance
                                </TableCell>
                                <TableCell className={classes.headerWeight} align="right" size="small" width="15%">
                                    Payment
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!journalEntriesLoading &&
                                invoices.length &&
                                invoices.map((invoice) => (
                                    <TableRow key={invoice.journalEntry.id} className={classes.tableRow}>
                                        <TableCell size="small">
                                            <Checkbox
                                                color="primary"
                                                checked={invoice.selected}
                                                onChange={() => toggleInvoice(invoice.journalEntry.id)}
                                            />
                                        </TableCell>
                                        <TableCell size="small">
                                            {invoice.journalEntry.invoicing?.invoiceNumber}
                                        </TableCell>
                                        <TableCell size="small">
                                            {invoice.journalEntry.invoicing?.dueDate
                                                ? moment(invoice.journalEntry.invoicing.dueDate).format('L')
                                                : ''}
                                        </TableCell>
                                        <TableCell align="right" size="small">
                                            {displayCurrencyAmount(invoice.journalEntry.invoicing!.amountInvoiced)}
                                        </TableCell>
                                        <TableCell align="right" size="small">
                                            {displayCurrencyAmount(getInvoiceBalance(invoice.journalEntry))}
                                        </TableCell>
                                        <TableCell size="small">
                                            <InputTextComponent
                                                id={`amountApplied-${invoice.journalEntry.id}`}
                                                disabled={!invoice.selected || externalPaymentSelected}
                                                size="small"
                                                fontSize="13"
                                                className={classes.textField}
                                                inputProps={classNames(classes.textFieldInput, classes.amountInput, {
                                                    [classes.textFieldInputDisabled]: !invoice.selected,
                                                })}
                                                onChange={(amount: string) =>
                                                    updateInvoiceAmount(invoice.journalEntry.id, amount)
                                                }
                                                value={Number(invoice.amount)}
                                                inputType={'currency'}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {!journalEntriesLoading && !invoices.length && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography>No Invoices</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {journalEntriesLoading && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Skeleton variant="text" width="100%" />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                        <TableFooter>
                            {!journalEntriesLoading && !!invoices.length && (
                                <TableRow>
                                    <TableCell
                                        align="right"
                                        size="small"
                                        colSpan={5}
                                        className={classes.tableFooterCell}
                                    >
                                        <Typography component="span" className={summaryLabel}>
                                            Total Applied:
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" size="small" className={classes.tableFooterCell}>
                                        <Typography component="span">
                                            {displayCurrencyAmount(allocatedTotal)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {paymentAmountValue > allocatedTotal && !externalPaymentSelected && (
                                <TableRow>
                                    <TableCell
                                        align="right"
                                        size="small"
                                        colSpan={5}
                                        className={classes.tableFooterCell}
                                    >
                                        <Typography component="span" className={summaryLabel}>
                                            Amount to Credit:
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" size="small" className={classes.tableFooterCell}>
                                        <Typography component="span">
                                            {externalPaymentSelected
                                                ? displayCurrencyAmount(0)
                                                : displayCurrencyAmount(paymentAmountValue - allocatedTotal)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableFooter>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button disableElevation onClick={() => onClose()}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    color="primary"
                    disabled={!isValid || updating}
                    onClick={handleAdd}
                >
                    Add
                </Button>
            </DialogActions>
        </>
    );
};

export default ReceivePaymentDialog;
